/* eslint-disable camelcase */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { isEmpty } from 'lodash/fp';
import PropTypes from 'prop-types';
import { format, utcToZonedTime } from 'date-fns-tz';
import { FaTrash } from 'react-icons/fa';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';

import Loader from '../../Components/Loader';
import {
  FETCH_EVENT_DETAIL,
  PARTICIPANTS_BY_COMPETITIONS,
  FETCH_EVENT_COMPETITION_ATTACHMENTS,
} from '../../GraphQL/Queries';
import { REMOVE_MEMBER } from '../../GraphQL/Mutations';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useCurrentUser from '../../Hooks/userCurrentUser';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import { Provider } from '../../Chakra/provider';

const DISPLAY_DATE_FORMAT = 'MM/dd/yyyy hh:mm aa';
const USER_LONG_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const USER_SHORT_TZ = format(new Date(), 'z');
const stringifyDate = (date) => format(utcToZonedTime(date, USER_LONG_TZ), DISPLAY_DATE_FORMAT);

const splitMistCode = (code) => {
  if (code.indexOf('-') >= 0) {
    return code.split('-')[1];
  }
  return code;
};

const Attachments = ({ attachments }) => (
  <Box padding={0}>
    {attachments?.length > 0 && (
      <>
        <Text color="gray.600" fontWeight="bold" marginBottom={2}>Submissions:</Text>
        {attachments.map((attachment) => (
          <AttachmentBlock key={attachment.id} attachment={attachment} />
        ))}
      </>
    )}
  </Box>
);

const AttachmentBlock = ({ attachment }) => (
  <VStack alignItems="flex-start" spacing={0}>
    <Link isExternal href={attachment.attachmentUrl}>
      {attachment.attachmentName}
    </Link>
    <Text color="gray.600" fontSize="sm">
      {`Uploaded at: ${stringifyDate(attachment.updatedAt)} ${USER_SHORT_TZ}`}
    </Text>
  </VStack>
);

const attachmentProp = {
  id: PropTypes.string.isRequired,
  attachmentName: PropTypes.string.isRequired,
  attachmentUrl: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
};

Attachments.propTypes = {
  attachments: PropTypes.arrayOf(attachmentProp).isRequired,
};

AttachmentBlock.propTypes = {
  attachment: PropTypes.shape(attachmentProp).isRequired,
};

const MemberRow = ({
  memberObj,
  attachments,
  isSelected,
  onSelect,
  isLoading,
  isRemoved,
  showCheckbox,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      {showCheckbox && (
        <GridItem borderBlockEnd={1} borderStyle="solid" borderColor="gray.200" paddingBlockEnd={1} marginBlockEnd={2}>
          <VStack justifyContent="center" height="100%">
            {isLoading ? (
              <Loader size="small" />
            ) : (
              <input
                type="checkbox"
                checked={isSelected || isRemoved}
                disabled={isLoading || isRemoved}
                onChange={() => onSelect(memberObj.memberId)}
              />
            )}
          </VStack>
        </GridItem>
      )}
      <GridItem
        colSpan={showCheckbox ? 1 : 2}
        borderBlockEnd={1}
        borderStyle="solid"
        borderColor="gray.200"
        paddingBlockEnd={1}
        marginBlockEnd={2}
        textDecoration={isRemoved ? 'line-through' : undefined}
      >
        <VStack alignItems="flex-start" spacing={0} marginBottom={2}>
          <Text>{`${memberObj.user.full_name} (${memberObj.user.code})`}</Text>
          <Text color="gray.600">{memberObj.school?.name}</Text>
          {!isEmpty(memberObj.member?.reason) && <Text color="gray.600">{memberObj.member?.reason}</Text>}
        </VStack>
      </GridItem>
      <GridItem borderBlockEnd={1} borderStyle="solid" borderColor="gray.200" paddingBlockEnd={1} marginBlockEnd={2}>
        <Attachments attachments={attachments} />
      </GridItem>
      <GridItem borderBlockEnd={1} borderStyle="solid" borderColor="gray.200" paddingBlockEnd={1} marginBlockEnd={2}>
        <VStack justifyContent="center" height="100%">
          <IconButton
            aria-label="Remove user from competition"
            icon={<FaTrash />}
            variant="ghost"
            colorScheme="red"
            onClick={onOpen}
            verticalAlign="center"
          />
        </VStack>
      </GridItem>
      {isOpen && (
        <ConfirmationModal isOpen={isOpen} onClose={onClose} memberId={memberObj.memberId} />
      )}
    </>
  );
};
MemberRow.propTypes = {
  memberObj: PropTypes.shape({
    id: PropTypes.string.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      full_name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
    }).isRequired,
    school: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    member: PropTypes.shape({
      reason: PropTypes.string,
      status: PropTypes.string.isRequired,
    }),
    memberId: PropTypes.string.isRequired,
  }).isRequired,
  attachments: PropTypes.arrayOf(attachmentProp),
  showCheckbox: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  isRemoved: PropTypes.bool,
};
MemberRow.defaultProps = {
  attachments: [],
  isSelected: false,
  onSelect: null,
  isLoading: false,
  isRemoved: false,
};

const ConfirmationModal = ({ isOpen, onClose, memberId }) => {
  const [removeUserFromCompetition, { loading }] = useMutation(REMOVE_MEMBER, {
    variables: { memberId: parseInt(memberId, 10) },
    refetchQueries: ['participantsByCompetition', 'FETCH_EVENT_COMPETITION_DETAIL'],
    awaitRefetchQueries: true,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Competitions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure you want to remove this user from the competition?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={removeUserFromCompetition} isLoading={loading}>
            Remove
          </Button>
          <Button colorScheme="gray" variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  memberId: PropTypes.string.isRequired,
};

const CompetitionDetailReport = () => {
  const {
    currentUser,
  } = useCurrentUser(true);
  const toast = useToast();
  const { id, competitionId } = useParams();
  const [isRemoving, setIsRemoving] = useState(false);
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkLoading, setBulkLoading] = useState({});
  const [bulkRemoved, setBulkRemoved] = useState({});

  const [removeUserFromCompetition] = useMutation(REMOVE_MEMBER);

  const { data, refetch: refetchEventDetail } = useQuery(FETCH_EVENT_DETAIL, {
    variables: {
      id: parseInt(id, 10),
    },
    skip: !currentUser || !id,
  });
  const event = data?.fetchEventDetail ?? {};

  const {
    data: participants,
    loading: participantsLoading,
    refetch: refetchParticipants,
  } = useQuery(
    PARTICIPANTS_BY_COMPETITIONS,
    {
      variables: { eventCompetitionId: parseInt(competitionId, 10) },
    },
  );

  const { data: attachments, loading: attachmentsLoading } = useQuery(
    FETCH_EVENT_COMPETITION_ATTACHMENTS,
    {
      variables: {
        id: parseInt(competitionId, 10),
      },
    },
  );

  useHeaderComponentsMutation({
    title: participants?.participantsByCompetition?.event_competition_title,
    backLink: `/events/${id}/competitions-report`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const handleSelect = (memberId) => {
    setBulkSelected((prev) => {
      const newSelected = { ...prev };
      if (newSelected[memberId]) {
        delete newSelected[memberId];
      } else {
        newSelected[memberId] = true;
      }
      return newSelected;
    });
  };

  const cancelRemoving = () => {
    setIsRemoving(false);
    setBulkSelected({});
  };

  const handleBulkRemove = async () => {
    await Promise.all(Object.keys(bulkSelected).map(async (memberId) => {
      setBulkLoading((prev) => ({ ...prev, [memberId]: true }));
      try {
        await removeUserFromCompetition({
          variables: { memberId: parseInt(memberId, 10) },
        });
        setBulkRemoved((prev) => ({ ...prev, [memberId]: true }));
      } catch (error) {
        toast({
          title: 'Error removing user from competition',
          position: 'top',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setBulkLoading((prev) => ({ ...prev, [memberId]: false }));
      }
    }));
    await Promise.all([refetchParticipants(), refetchEventDetail()]);
    setIsRemoving(false);
    setBulkSelected({});
    setBulkLoading({});
    setBulkRemoved({});
    toast({
      title: 'Selected users have been removed from the competition',
      position: 'top',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };


  if (attachmentsLoading || participantsLoading) {
    return <Loader />;
  }

  const attachmentsByCode = {};
  attachments.fetchEventCompetitionDetail.allJudgables.forEach((j) => {
    if (j.members?.length > 0) {
      j.members.forEach((member) => {
        attachmentsByCode[splitMistCode(member.code)] = member.attachments;
      });
    } else {
      attachmentsByCode[splitMistCode(j.code)] = j.attachments;
    }
  });

  return (
    <VStack spacing={4} paddingBlockStart={4}>
      <HStack justifyContent="space-around" width="100%">
        <VStack>
          <Text fontSize="2xl" fontWeight="bold">{participants?.participantsByCompetition.joined_count}</Text>
          <Text>Joined</Text>
        </VStack>
        <VStack>
          <Text fontSize="2xl" fontWeight="bold">{participants?.participantsByCompetition.waitlisted_count}</Text>
          <Text>Waitlisted</Text>
        </VStack>
      </HStack>
      <Grid templateColumns="50px 1fr 1fr auto" width="100%">
        <GridItem colSpan={4}>
          <Heading as="h2" size="md" marginTop={4} marginBottom={2} width="100%">
            {`Joined (${participants?.participantsByCompetition.joined_count})`}
          </Heading>
        </GridItem>
        {participants?.participantsByCompetition.joined_user.map((obj) => (
          <MemberRow
            key={obj.memberId}
            memberObj={obj}
            attachments={attachmentsByCode[obj.user.code]}
          />
        ))}
        {participants?.participantsByCompetition.waitlisted_count > 0 && (
          <GridItem colSpan={4}>
            <HStack alignItems="baseline">
              <Heading as="h2" size="md" marginTop={4} marginBottom={2}>
                {`Waitlisted (${participants?.participantsByCompetition.waitlisted_count})`}
              </Heading>
              {isRemoving ? (
                <>
                  <Button variant="ghost" height="auto" paddingBlock={2} onClick={cancelRemoving} disabled={Object.keys(bulkLoading).length > 0}>
                    Cancel
                  </Button>
                  <Button variant="ghost" colorScheme="red" height="auto" paddingBlock={2} onClick={handleBulkRemove} disabled={Object.keys(bulkLoading).length > 0 || Object.keys(bulkSelected).length === 0}>
                    Confirm Bulk Remove
                  </Button>
                </>
              ) : (
                <Button variant="ghost" colorScheme="red" height="auto" paddingBlock={2} onClick={() => setIsRemoving(true)}>
                  Bulk Remove
                </Button>
              )}
            </HStack>
          </GridItem>
        )}
        {participants?.participantsByCompetition.waitlist_user.map((obj) => (
          <MemberRow
            key={obj.memberId}
            memberObj={obj}
            attachments={attachmentsByCode[obj.user.code]}
            showCheckbox={isRemoving}
            onSelect={handleSelect}
            isSelected={bulkSelected[obj.memberId]}
            isLoading={bulkLoading[obj.memberId]}
            isRemoved={bulkRemoved[obj.memberId]}
          />
        ))}
      </Grid>
    </VStack>
  );
};

export default () => (
  <Provider>
    <CompetitionDetailReport />
  </Provider>
);
